/** The "Me" pages: My salary, My info, My supervisor, My equipment. Every request reads the person off the session; none takes a person id, so a page cannot ask about a colleague. Decisions are pure helpers because vitest cannot render a component. */
import { useQuery } from '@tanstack/react-query'

import { api } from './api'
import type { MyPayslip } from './payroll'


export type PeriodMode = 'month' | 'range'

/** A `YYYY-MM` month, the value an `<input type="month">` gives. */
const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/

/** The `start`/`end` the payslip index is asked for, or null while no usable period is picked -- and null means NOTHING IS FETCHED. A start after the end is null too rather than swapped: a result for a different period would contradict what the inputs show. */
export function periodQuery(
  mode: PeriodMode,
  month: string,
  from: string,
  to: string,
): { start: string; end: string } | null {
  if (mode === 'month') return MONTH.test(month) ? { start: month, end: month } : null
  if (!MONTH.test(from) || !MONTH.test(to)) return null
  return from <= to ? { start: from, end: to } : null
}

/** What stands in for an amount while amounts are hidden. */
export const MASK = '••••'

/** An amount as shown: itself, or the mask when hidden -- a dash or "view" label would give away which months have a figure. */
export function masked(shown: boolean, text: string): string {
  return shown ? text : MASK
}

export function useMyPayslipsInPeriod(period: { start: string; end: string } | null) {
  return useQuery({
    queryKey: ['me', 'payslips', period?.start ?? null, period?.end ?? null],
    queryFn: async () =>
      (await api.get<MyPayslip[]>('/api/me/payslips', { params: period ?? undefined })).data,
    enabled: period != null,
  })
}


export type MyInfo = {
  /** False for someone with a login and no personnel record; every section then holds nulls, shown as "not recorded". */
  has_record: boolean
  display_name: string
  personal: Record<string, string | null>
  contact: Record<string, string | null>
  employment: Record<string, string | number | string[] | null>
  emergency: Record<string, string | null>
}

export function useMyInfo() {
  return useQuery({
    queryKey: ['me', 'info'],
    queryFn: async () => (await api.get<MyInfo>('/api/me/info')).data,
  })
}

/** One field as shown: empty string, null or empty list is "not recorded" (null here), never a blank cell. A number such as `staff_id` is shown as itself, including 0. */
export function infoValue(value: string | number | string[] | null | undefined): string | null {
  if (value == null) return null
  if (Array.isArray(value)) return value.length ? value.join(', ') : null
  if (typeof value === 'number') return String(value)
  return value.trim() ? value : null
}


export type Colleague = { id: string; display_name: string; position: string; email: string; phone: string }
/** `null` is a field the server withheld from this caller, never a zero. */
export type ReportSummary = {
  team: string | null
  work_mode_today: string | null
  pending_logs: number | null
  pending_leave: number | null
  month_hours: number | null
}
export type Report = Colleague & { summary?: ReportSummary }
type MyTeam = { supervisor: Colleague | null; reports: Report[] }

export function useMyTeam() {
  return useQuery({
    queryKey: ['me', 'team'],
    queryFn: async () => (await api.get<MyTeam>('/api/me/team')).data,
  })
}


export type MyEquipmentRow = {
  id: string
  current: boolean
  asset_tag: string
  model: string
  brand: string
  receive_date: string | null
  return_date: string | null
  status: string
  accessories: string[]
  note: string
}

export function useMyEquipment() {
  return useQuery({
    queryKey: ['me', 'equipment'],
    queryFn: async () => (await api.get<MyEquipmentRow[]>('/api/me/equipment')).data,
  })
}

/** The server orders current first, then newest hand-out; the page shows two sections, so it splits rather than re-sorts. */
export function splitEquipment(rows: MyEquipmentRow[]): {
  current: MyEquipmentRow[]
  history: MyEquipmentRow[]
} {
  return { current: rows.filter((r) => r.current), history: rows.filter((r) => !r.current) }
}

export type MyFundMonth = { month: number; status: 'paid' | 'unpaid' | 'not_due'; amount: string | number | null }
export type MyFundYear = {
  year: number
  in_fund: boolean
  months: MyFundMonth[]
  paid_months: number
  total: string | number
  points: string | number
}

/** The caller's own activity-fund months; the server takes the person from the session. */
export function useMyFundYear(year: number) {
  return useQuery({
    queryKey: ['me', 'activity-fund', year],
    queryFn: async () =>
      (await api.get<MyFundYear>('/api/activity-fund/contributions/me', { params: { year } })).data,
  })
}
